import { Link } from "react-router-dom";

export default function PolicyPage({ title, kicker, blocks }) {
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10" data-testid="policy-page">
            <p className="font-mono-ui text-[#857C72]">{kicker}</p>
            <h1 className="font-display text-5xl md:text-6xl mt-2">{title}</h1>
            <div className="mt-10 dw-section p-8 sm:p-10 space-y-6">
                {blocks.map((b, i) => (
                    <div key={i}>
                        {b.h && <h2 className="font-display text-2xl mb-2">{b.h}</h2>}
                        {b.p && <p className="text-[#3a342e] leading-relaxed">{b.p}</p>}
                        {b.list && (
                            <ul className="list-disc pl-5 space-y-1.5 text-[#3a342e]">
                                {b.list.map((x, j) => <li key={j}>{x}</li>)}
                            </ul>
                        )}
                    </div>
                ))}
            </div>
            <div className="mt-10 text-center text-sm text-muted-foreground">
                Questions? <Link to="/contact" className="underline">Contact our team</Link>.
            </div>
            <div className="h-24" />
        </div>
    );
}

export function PrivacyPage() {
    return <PolicyPage
        kicker="Help" title="Privacy Policy"
        blocks={[
            { p: "X-Plosiv Event & Promotions Pvt. Ltd. respects your privacy. This policy explains how information shared through our website and enquiry form may be used. Last updated: " + new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" }) },
            { h: "Information we collect", list: ["Contact details you submit, such as your name, email and phone number", "Event details you choose to share, such as occasion, date, location and guest estimate", "Account or transaction details if you use a connected service"] },
            { h: "How we use it", list: ["To respond to event enquiries and prepare proposals", "To coordinate services you have requested", "To maintain, protect and improve our website"] },
            { h: "Sharing and retention", list: ["We share information only with relevant team members and event partners when needed to respond to your brief", "We retain information for operational and legal purposes", "We do not sell your personal data"] },
            { h: "Your rights", list: ["Access — ask us what we hold about you", "Delete — ask us to remove your account & data", "Rectify — update or correct your info", "Export — get a copy of your data"] },
            { p: "For a privacy request, contact nitin.xplosiv@gmail.com." },
        ]} />;
}

export function TermsPage() {
    return <PolicyPage
        kicker="Help" title="Terms of Service"
        blocks={[
            { p: "By using the X-Plosiv website, you agree to use its enquiry and information services lawfully. Event services are confirmed only through a written proposal or agreement." },
            { h: "Enquiries and proposals", p: "Submitting an enquiry does not confirm a booking. Scope, availability, timelines and fees are confirmed in the proposal or agreement issued for your event." },
            { h: "Changes and cancellation", p: "Changes, cancellation terms and payment schedules are set out in the applicable event proposal or signed agreement. Please contact our team as early as possible if plans change." },
            { h: "Intellectual property", p: "Website text, photography, branding and other content belong to X-Plosiv or their respective owners. Please obtain written permission before reusing commercial content." },
            { h: "Governing law", p: "These terms are governed by the laws of India, subject to the terms of any applicable written agreement." },
        ]} />;
}

export function CookiesPage() {
    return <PolicyPage
        kicker="Help" title="Cookies Policy"
        blocks={[
            { p: "Cookies are small text files stored on your device when you visit a site. They help us remember you and improve your experience." },
            { h: "Cookies we use", list: ["Essential — required for cart, login, checkout (cannot be disabled)", "Analytics — anonymised usage to improve the site", "Marketing — to show relevant ads on other platforms (with consent)"] },
            { h: "Manage cookies", p: "You can block or delete cookies in your browser settings. Note: some site features may not work without essential cookies." },
        ]} />;
}

export function ShippingPage() {
    return <PolicyPage
        kicker="Event information" title="Event logistics"
        blocks={[
            { h: "Where we work", p: "Our team is based in New Delhi and can discuss event requirements across India. Venue, travel and production scope depend on the event brief." },
            { h: "Production timelines", p: "Lead times depend on event format, venue and services required. Share your preferred date early so we can confirm feasibility with the right partners." },
            { h: "Venue and suppliers", p: "Venue access, supplier schedules, setup windows and on-site logistics are coordinated during planning and confirmed in your event plan." },
        ]} />;
}

export function ReturnsPage() {
    return <PolicyPage
        kicker="Event information" title="Changes & cancellations"
        blocks={[
            { p: "Event plans can evolve. Contact your X-Plosiv coordinator as soon as possible so we can review the updated requirements." },
            { h: "Changes to your brief", p: "Date, venue, guest-count and production changes may affect supplier availability, scope and fees. Your coordinator will confirm any revised plan in writing." },
            { h: "Cancellation terms", p: "Cancellation schedules and any applicable costs are set out in your event proposal or signed agreement. Please refer to that document for the terms that apply to your booking." },
            { h: "Need help?", p: "Email nitin.xplosiv@gmail.com or call +91 98105 25439 with your event details." },
        ]} />;
}

export function SizingPage() {
    const planningNotes = ["Occasion and purpose", "Preferred date and time", "Venue or location ideas", "Approximate guest count", "Must-have moments or services", "Budget range and decision timeline"];
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10" data-testid="sizing-page">
            <p className="font-mono-ui text-[#857C72]">Help</p>
            <h1 className="font-display text-5xl md:text-6xl mt-2">A useful first brief</h1>
            <p className="text-[#6B645C] mt-3 max-w-xl">You do not need every detail figured out. A few starting points help us shape the right conversation.</p>
            <div className="mt-10 dw-section p-6 sm:p-8 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs font-mono-ui text-muted-foreground border-b border-[#E7DED2]">
                            <th className="py-3">A few helpful details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {planningNotes.map(note => <tr key={note} className="border-b border-[#F4EFE6]"><td className="py-3 font-medium">{note}</td></tr>)}
                    </tbody>
                </table>
            </div>
            <div className="mt-8 dw-section p-6 sm:p-8">
                <h3 className="font-display text-2xl">Not sure where to start?</h3>
                <p className="text-[#3a342e] mt-3 leading-relaxed">Tell us what you know today. Our team can help turn the first idea into a clear event plan.</p>
            </div>
            <div className="h-24" />
        </div>
    );
}

export function FAQPage() {
    const items = [
        { q: "What kinds of events do you plan?", a: "Corporate events, weddings, exhibitions, MICE, catering, venue experiences, gifting, printing and promotions." },
        { q: "How do I get a proposal?", a: "Send us your event type, preferred date, location, guest estimate and any early ideas. Our team will follow up to understand the brief." },
        { q: "Do you work outside Delhi?", a: "We are based in New Delhi and can discuss event requirements across India. Travel and production scope are confirmed for each brief." },
        { q: "Can you help with only one part of an event?", a: "Yes. We can discuss a focused service or coordinate a complete event, depending on your needs." },
        { q: "When is a booking confirmed?", a: "A booking is confirmed through the written proposal or agreement after scope, availability and payment terms are agreed." },
        { q: "Can event details change after planning starts?", a: "Contact your coordinator as soon as plans change. We will review supplier availability and any impact on the agreed scope." },
        { q: "How do I reach the team?", a: "Email nitin.xplosiv@gmail.com or call +91 98105 25439." },
    ];
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10" data-testid="faq-page">
            <p className="font-mono-ui text-[#857C72]">Help</p>
            <h1 className="font-display text-5xl md:text-6xl mt-2">FAQ</h1>
            <div className="mt-10 space-y-3">
                {items.map((it, i) => (
                    <details key={i} className="dw-section group px-6 py-5 cursor-pointer">
                        <summary className="font-display text-xl flex justify-between items-center">
                            {it.q}
                            <span className="text-2xl group-open:rotate-45 transition-transform">+</span>
                        </summary>
                        <p className="text-[#3a342e] mt-3 leading-relaxed">{it.a}</p>
                    </details>
                ))}
            </div>
            <div className="h-24" />
        </div>
    );
}
